import { Icon, IconSet } from '@postscript/components';
import { useGetUserProfile } from 'components/account/AccountView/users/hooks/useUsers';
import useAccountStatus from 'components/account/useAccountStatus';
import { AnnouncementType } from 'components/admin/Announcements/utils';
import { OOI_USE_TWILIO_VERIFY } from 'components/admin/utils/feature-flags';
import { useBanners } from 'components/GlobalBanners/globalBanners';
import { useGlobalModal } from 'components/GlobalModal/globalModal';
import {
  HELP_ARTICLES,
  SUPPORTED_DSC_COUNTRY_CODES,
} from 'constants/constants';
import { usePSLabs } from 'controllers/contexts/labsFeatures';
import { useUser } from 'controllers/contexts/user';
import { useCallback, useEffect } from 'react';
import { PhoneNumberData } from 'types';
import { BANNER_TYPES } from 'utils/events';
import { useGetTFNVerification } from './hooks/useTFNVerification';
import InvalidBusinessAddressModal from './TFNReverificationModals/InvalidBusinessAddressModal';
import InvalidBusinessEmailModal from './TFNReverificationModals/InvalidBusinessEmailModal';
import InvalidBusinessInfoModal from './TFNReverificationModals/InvalidBusinessInfoModal';
import InvalidBusinessRegistrationModal from './TFNReverificationModals/InvalidBusinessRegistrationModal';
import InvalidContactModal from './TFNReverificationModals/InvalidContactModal';
import WebsiteInaccessibleModal from './TFNReverificationModals/WebsiteInaccessibleModal';
import TFNVerificationSubmissionModal from './TFNVerificationSubmissionModal';
import { TFNReverificationModalType } from './types';
import {
  buildVerificationMailto,
  getTFNReverificationModalType,
  isEligibleForResubmission,
} from './utils';

const BlueInformationIcon = () => (
  <Icon
    component={IconSet.Information}
    color="var(--highlight-color)"
    size={24}
  />
);

function hasValidNumber(phoneNumbers: PhoneNumberData): boolean {
  // Shops with unverified TFNs can send messages via a short code or with an international long code (excluding Canada)

  const hasShortCode = SUPPORTED_DSC_COUNTRY_CODES.some((countryCode) => {
    const key = `${countryCode.toLocaleLowerCase()}_short_code` as const;
    const phoneNumber = phoneNumbers[key] ?? '';

    // Matches messaging logic for short code validation in the backend
    return phoneNumber.length > 3 && phoneNumber.length < 7;
  });

  // Get any non-CA international long code
  const hasIntlLongCode = !!Array.from(Object.keys(phoneNumbers)).find(
    (phoneNumberKey) => {
      return !['us', 'ca'].includes(phoneNumberKey.substring(0, 2));
    },
  );

  return hasShortCode || hasIntlLongCode;
}

const tfnVerificationBannerId = 'tfnVerificationBanner';

export default function TFNVerificationStatusBanner(): null {
  const { addBanner, removeBanner } = useBanners();
  const { showModal } = useGlobalModal();
  const { tfnVerificationStatus, isFetched } = useAccountStatus();
  const { data: verificationRequest } = useGetTFNVerification();
  const status = tfnVerificationStatus ?? '';
  const rejectionCode = verificationRequest?.rejection_code ?? null;
  const {
    user: { shop_name: shopName, custom_phone_number_data: phoneNumbers },
  } = useUser();
  const { data: profile } = useGetUserProfile();
  const { hasLabsFlag } = usePSLabs();
  const hasTwilioVerifyFeatureFlag = hasLabsFlag(OOI_USE_TWILIO_VERIFY);
  const canResubmit = isEligibleForResubmission(rejectionCode);

  const openReverificationModal = useCallback(() => {
    const modalType = getTFNReverificationModalType(rejectionCode);

    switch (modalType) {
      case TFNReverificationModalType.INVALID_BUSINESS_REGISTRATION:
        showModal({
          children: <InvalidBusinessRegistrationModal />,
        });
        break;
      case TFNReverificationModalType.INVALID_BUSINESS_EMAIL:
        showModal({
          children: <InvalidBusinessEmailModal />,
        });
        break;
      case TFNReverificationModalType.INVALID_CONTACT:
        showModal({
          children: <InvalidContactModal />,
        });
        break;
      case TFNReverificationModalType.INVALID_ADDRESS:
        showModal({
          children: <InvalidBusinessAddressModal />,
        });
        break;
      case TFNReverificationModalType.INVALID_BUSINESS_INFO:
        showModal({
          children: <InvalidBusinessInfoModal />,
        });
        break;
      case TFNReverificationModalType.UNREACHABLE_WEBSITE:
        showModal({
          children: (
            <WebsiteInaccessibleModal denialReason="Website is not accessible, established, or active enough for carrier review." />
          ),
        });
        break;
      case TFNReverificationModalType.PASSWORD_PROTECTED_WEBSITE:
        showModal({
          children: (
            <WebsiteInaccessibleModal denialReason="Website is password-protected or requires login, which prevents carriers from reviewing it." />
          ),
        });
        break;
      default:
        // For DENIED status with an unknown rejection codes, or a FAILED status, default to registration modal
        showModal({
          children: (
            <InvalidBusinessRegistrationModal title="Toll-Free Number verification failed" />
          ),
        });
        break;
    }
  }, [rejectionCode, showModal]);

  useEffect(() => {
    removeBanner(tfnVerificationBannerId);

    if (
      hasTwilioVerifyFeatureFlag ||
      !phoneNumbers ||
      !isFetched ||
      hasValidNumber(phoneNumbers) ||
      !shopName
    ) {
      return;
    }

    if (status === 'INVALID') {
      return addBanner({
        id: tfnVerificationBannerId,
        announcementType: AnnouncementType.EMERGENCY,
        eventTrackingOptions: {
          bannerType: BANNER_TYPES.TFN_VERIFICATION_STATUS,
        },
        data: {
          collapseAt: 1200,
          variant: 'critical',
          heading:
            'Action required: Missing or invalid Information for TFN verification',
          bodyText: (
            <>
              We couldn&apos;t verify your toll-free number due to missing or
              invalid information. Please review your details and retry
              submission to activate your TFN.
            </>
          ),
          primaryAction: {
            text: 'Retry Submission',
            onClick: () => {
              showModal({
                children: <TFNVerificationSubmissionModal />,
              });
            },
          },
          secondaryAction: {
            text: 'Learn More',
            onClick: () => window.open(HELP_ARTICLES.tfnVerification, '_blank'),
          },
        },
      });
    }

    if (['CREATED', 'VALID', 'SUBMITTED', 'REVIEWED'].includes(status)) {
      return addBanner({
        id: tfnVerificationBannerId,
        eventTrackingOptions: {
          bannerType: BANNER_TYPES.TFN_VERIFICATION_STATUS,
        },
        customIcon: BlueInformationIcon,
        data: {
          collapseAt: 1200,
          variant: 'default',
          heading: 'TFN submitted for verification',
          bodyText: (
            <>
              Before collecting subscribers or sending messages, US mobile
              carriers require business verification to activate your Toll-Free
              Number (TFN). This process typically takes up to 5 business days.
            </>
          ),
          primaryAction: {
            text: 'Learn More',
            onClick: () => window.open(HELP_ARTICLES.tfnVerification, '_blank'),
          },
        },
      });
    }

    if (status === 'FAILED') {
      return addBanner({
        id: tfnVerificationBannerId,
        announcementType: AnnouncementType.EMERGENCY,
        eventTrackingOptions: {
          bannerType: BANNER_TYPES.TFN_VERIFICATION_STATUS,
        },
        data: {
          collapseAt: 1200,
          variant: 'critical',
          heading: 'Toll-Free Number (TFN) verification failed',
          bodyText: (
            <>
              Your Toll-Free Number verification was denied because your
              Business Registration Number or business address is incomplete or
              invalid. Please update your information and resubmit. Verification
              typically takes up to 5 business days, and your website must
              remain live during this time.
            </>
          ),
          primaryAction: {
            text: 'Fix and Resubmit',
            onClick: openReverificationModal,
          },
          secondaryAction: {
            text: 'Contact Support',
            onClick: () =>
              window.open(
                buildVerificationMailto(
                  shopName,
                  profile?.firstName ?? undefined,
                ),
                '_blank',
              ),
          },
        },
      });
    }

    if (['FLAGGED', 'DENIED'].includes(status)) {
      const contactSupportAction = {
        text: 'Contact Support',
        onClick: () =>
          window.open(
            buildVerificationMailto(shopName, profile?.firstName ?? undefined),
            '_blank',
          ),
      };

      return addBanner({
        id: tfnVerificationBannerId,
        announcementType: AnnouncementType.EMERGENCY,
        eventTrackingOptions: {
          bannerType: BANNER_TYPES.TFN_VERIFICATION_STATUS,
        },
        data: {
          collapseAt: 1200,
          variant: 'critical',
          heading: 'Toll-Free Number (TFN) verification denied',
          bodyText:
            "You can't send messages or collect subscribers until verification is complete. Fix and resubmit your TFN information to avoid blocked messages.",
          primaryAction: canResubmit
            ? {
                text: 'Fix and Resubmit',
                onClick: openReverificationModal,
              }
            : contactSupportAction,
          ...(canResubmit && { secondaryAction: contactSupportAction }),
        },
      });
    }
  }, [
    addBanner,
    removeBanner,
    showModal,
    phoneNumbers,
    status,
    isFetched,
    shopName,
    hasTwilioVerifyFeatureFlag,
    canResubmit,
    profile?.firstName,
    openReverificationModal,
    rejectionCode,
  ]);

  return null;
}
